import { Unsubscribe } from '@reduxjs/toolkit';
import { setBasePath } from '@shoelace-style/shoelace/dist/utilities/base-path.js';
import { setUserId } from 'firebase/analytics';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { LitElement, PropertyValueMap, css, html} from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { analytics, auth } from './firebase';
import { setupLayoutListeners } from './media-query';
import { router } from './router';
import { Layout, PAGE, selectAppHeaderHeight, selectCurrentPage, selectLayout, selectProductSearchQueryArgs } from './state/pages';
import { AppUser, login, logout, selectUser } from './state/users';
import { RootState, store } from './store';
import { styles as sharedStyles } from './styles/shared-styles';

import '@shoelace-style/shoelace/dist/components/icon-button/icon-button';
import { ProductSearchQueryArgs } from './api/models';
import './components/app-header';
import { SupermarktEvents, handleAddToList, handleNavigate, handleRemoveFromList, handleSearch } from './events';
import { getSelectedListCount, getSelectedListName } from './state/products';
import { getProductStats } from './api/products';

setBasePath("https://cdn.jsdelivr.net/npm/@shoelace-style/shoelace@2.14.0/cdn/");

@customElement('app-index')
export class AppIndex extends LitElement {

  static styles = [
    sharedStyles,
    css`
    :host {
      display: block;
      padding: 0px;
      margin: 0px 0px 500px 0px;
      box-sizing: border-box; 
      position: relative;
      background-color: var(--sl-color-primary-50);
      width: 100vw;
      height: 100vh;
    }

    #bg-image {
      z-index: -3;
    }

    #bg-video {
      filter: blur(10px); /* Apply blur to the video */
      z-index: -2;
    }

    #overlay {
      z-index: -1;
    }

    .background {
      position: absolute;
      top: -100px;
      left: 0;
      width: 100%;
      height: calc(100% + 100px);
      object-fit: cover; /* Ensures the video covers the whole screen */
    }

    /* Overlay to dim the background a bit */
    #overlay {
      background: var(--sl-color-primary-400); /* Dark overlay */
      opacity: 0.6;
    }
    
  
    app-header {
      position: fixed;
      top: 0;
      z-index: 600;
    }

    .body {
      box-sizing: border-box; 
      display: block;
      scrollbar-color: var(--sl-color-primary-600) var(--sl-color-primary-100);
     }    

  `];

  @property({ attribute: false, type: Object })
  user: AppUser | null = null;

  @property({ attribute: false, type: String })
  title: string = "Supermarktprijzen";

  @property({ attribute: false, type: Number })
  layout: number = Layout.PHONE;

  @property({ attribute: false, type: String })
  watchlistName: string = "";

  @property({ attribute: false, type: Number })
  watchlistCount: number = 0;

  @property({ attribute: false, state: true, type: Number })
  private appHeaderHeight: number = 40;

  @property({ attribute: false, state: true, type: Object })
  private page: PAGE = PAGE.WELCOME;

  @property({ attribute: false, state: true, type: Object })
  private productSearchQueryArgs: ProductSearchQueryArgs = {};

  private stateUnsubscribe: Unsubscribe = () => { };

  stateChanged(state: RootState) {
    this.user = selectUser(state);
    this.layout = selectLayout(state);
    this.page = selectCurrentPage(state);
    this.watchlistName = getSelectedListName(state);
    this.watchlistCount = getSelectedListCount(state);
    this.appHeaderHeight = selectAppHeaderHeight(state);
    this.productSearchQueryArgs = selectProductSearchQueryArgs(state);
  }

  connectedCallback() {
    super.connectedCallback();
    this.stateUnsubscribe = store.subscribe(() => {
      const state = store.getState();
      this.stateChanged(state);
    });
    // update immediately
    const state = store.getState();
    this.stateChanged(state);
    this.addEventListener(SupermarktEvents.NAVIGATE, handleNavigate);
    this.addEventListener(SupermarktEvents.ADD_TO_LIST, handleAddToList);
    this.addEventListener(SupermarktEvents.REMOVE_FROM_LIST, handleRemoveFromList);
    this.addEventListener(SupermarktEvents.SEARCH, handleSearch);
  }

  disconnectedCallback() {
    this.stateUnsubscribe();
    this.removeEventListener(SupermarktEvents.NAVIGATE, handleNavigate);
    this.removeEventListener(SupermarktEvents.ADD_TO_LIST, handleAddToList);
    this.removeEventListener(SupermarktEvents.REMOVE_FROM_LIST, handleRemoveFromList);
    this.removeEventListener(SupermarktEvents.SEARCH, handleSearch);
  }

  constructor() {
    super();
    store.dispatch(getProductStats());
    onAuthStateChanged(auth, (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        const user = firebaseUser.toJSON() as AppUser;
        store.dispatch(login(user)); // login
        setUserId(analytics, user.uid); // track users
        console.info(`user with uid=${user.uid} logged in.`);
      } else {
        store.dispatch(logout());
        setUserId(analytics, null);
      }
    });
    setupLayoutListeners();
  }

  render() {
    return html`
      <!-- <img id="bg-image" class="background" src="/assets/img/video-background-snap.jpg" />
      <video autoplay muted preload="auto" id="bg-video" class="background" id="bg-video" style=${this.page === PAGE.WELCOME ? "display: block; visibility: visible; opacity: 1;" : "display: none;  visibility: hidden; opacity: 0;"}>
          <source id="bg-video-source" type="video/mp4">
      </video>
      <div id="overlay" class="background"></div> -->
      <app-header 
        style="height: ${this.appHeaderHeight}px"
        .user=${this.user} 
        .layout=${this.layout}
        .title=${router.context.title}
        .watchlistCount=${this.watchlistCount}
        .watchlistName=${this.watchlistName}
        .page=${this.page}
        .productSearchQuery=${this.productSearchQueryArgs}
      >
      </app-header>
      <div class="body" style="margin-top: ${this.appHeaderHeight}px; height: calc(100vh - ${this.appHeaderHeight}px); overflow-y: scroll; overflow-x: hidden; padding-bottom: 200px;">
     
        ${router.render()}
      </div>
        `;
  }

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    // const video: HTMLVideoElement = this.shadowRoot?.getElementById("bg-video") as HTMLVideoElement;
    // const source: HTMLSourceElement = this.shadowRoot?.getElementById("bg-video-source") as HTMLSourceElement;
    // video.addEventListener('loadeddata', () => {
    //   // Append the video to the container once it is loaded
    //   console.info('Background video loaded');
    //   video.classList.add("loaded");
    //   video.playbackRate = 0.1;
    // });
    // source.src = "/assets/video/background.mp4"
    router.addEventListener('route-changed', () => {
      if ("startViewTransition" in document) {
        (document as any).startViewTransition(() => this.requestUpdate());
      }
      else {
        this.requestUpdate();
      }
    });
  }


} 
